/* ========================================================================
   INFORMATIONS GÉNÉRALES SUR LE SITE
   Propriété de © 2019/2024 Shopiweb.fr
   Pour plus d'informations, visitez : https://www.shopiweb.fr
   ======================================================================== */

/* =====================
   Recharger l'ensemble de la collection de produits avec un contenu actualisé
   ===================== */
let collectionRequest = null;
let collectionRevision = 0;
let paginationBusy = false;
let autoPaginationObserver = null;

const fetchCollectionDocument = async (url, signal) => {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`Collection HTTP ${response.status}`);
  return new DOMParser().parseFromString(await response.text(), "text/html");
};

const collectionError = (anchor) => {
  let notice = document.querySelector("#collection-load-error");
  if (!notice) {
    notice = document.createElement("p");
    notice.id = "collection-load-error";
    notice.className = "alert alert-warning mt-3";
    notice.setAttribute("role", "alert");
    anchor.insertAdjacentElement("afterend", notice);
  }
  notice.textContent = "Chargement interrompu. Réessayez ou ";
  const link = document.createElement("a");
  link.href = window.location.href;
  link.textContent = "rechargez la page";
  notice.append(link);
};

const notifyCollectionUpdate = () => {
  if (window.bootstrap?.Popover) {
    document.querySelectorAll('.collection [data-bs-toggle="popover"]').forEach((el) => {
      bootstrap.Popover.getOrCreateInstance(el);
    });
  }
  window.dispatchEvent(new CustomEvent("onCollectionShopiwebUpdate"));
};

const reloadCollection = async () => {
  collectionRequest?.abort();
  const controller = new AbortController();
  collectionRequest = controller;
  const revision = ++collectionRevision;
  const listing = document.querySelector(".collection .product-listing");
  if (!listing) return;
  listing.style.opacity = "0.5";
  listing.setAttribute("aria-busy", "true");
  autoPaginationObserver?.disconnect();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const doc = await fetchCollectionDocument(window.location.href, controller.signal);
    if (revision !== collectionRevision) return;
    const updated = doc.querySelector(".collection");
    if (!updated) throw new Error("Missing collection");
    document.querySelector(".collection").replaceWith(updated);
    updateUIComponents(doc);
    notifyCollectionUpdate();
  } catch (error) {
    if (revision === collectionRevision) collectionError(listing);
  } finally {
    clearTimeout(timeout);
    if (revision === collectionRevision) {
      listing.style.opacity = "";
      listing.removeAttribute("aria-busy");
      collectionRequest = null;
    }
  }
};

// Updates filter components and other interactive elements within the offcanvas filters
const updateUIComponents = (updatedDocument) => {
  // Update collapsible filter internals
  document
    .querySelectorAll("#offcanvas-filters .collapse-inner")
    .forEach((collapse) => {
      const collapseId = collapse.closest(".collapse").getAttribute("id");
      const newCollapseContent = updatedDocument.querySelector(
        `#offcanvas-filters #${collapseId} .collapse-inner`
      );
      if (newCollapseContent) {
        collapse.replaceWith(newCollapseContent);
      }
    });

  // Replace specific filter UI components such as footer, clear all button, and sort by selector
  const uiSelectors = [
    "#offcanvas-filters .offcanvas-footer",
    "#offcanvas-filters .btn-filters-clear-all",
    '#offcanvas-filters [name="sort_by"]',
  ];
  uiSelectors.forEach((selector) => {
    const newElement = updatedDocument.querySelector(selector);
    if (newElement) document.querySelector(selector)?.replaceWith(newElement);
  });
};

/* =====================
   Utilitaire pour mettre à jour l'URL avec de nouveaux paramètres de requête
   ===================== */
const updateUrlWithQueryParams = (form) => {
  const params = new URLSearchParams(new FormData(form));
  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState({}, "", newUrl);
};

/* =====================
   Mise à jour de la collection en fonction des changements de filtre
   ===================== */
window.onChangeCollectionFilter = async (inputElement) => {
  const form = inputElement.closest("form");
  updateUrlWithQueryParams(form);
  await reloadCollection();
};

/* =====================
   Gestion des changements de filtres de prix de la collection
   ===================== */
const setupPriceFilterListeners = () => {
  document.querySelectorAll(".filter-amounts input").forEach((input) => {
    if (input.dataset.priceListener === "true") return;
    input.dataset.priceListener = "true";
    input.addEventListener(
      "input",
      window.debounce(async () => {
        updateUrlWithQueryParams(input.closest("form"));
        await reloadCollection();
      }, 750)
    );
  });
};
setupPriceFilterListeners();
window.addEventListener(
  "onCollectionShopiwebUpdate",
  setupPriceFilterListeners
);

/* =====================
   Développe tous les filtres cachés en cliquant sur le bouton
   ===================== */
window.onClickFiltersViewMore = (button) => {
  button
    .closest(".collapse")
    .querySelectorAll(".form-check")
    .forEach((check) => {
      check.removeAttribute("hidden");
    });
  button.remove();
};

/* =====================
   Efface tous les filtres et recharge la collection
   ===================== */
window.onClickClearAllFilters = async (button) => {
  const form = button.closest("form");
  const sortValue = form.querySelector('[name="sort_by"]').value;
  const params = new URLSearchParams();
  params.set("sort_by", sortValue);

  // Le filtre de disponibilité imposé par le thème (option « Masquer les
  // produits en rupture ») n'est pas effaçable par le visiteur : on le
  // reconduit après un « Tout effacer ».
  const forcedAvailability = form.querySelector(
    'input[type="hidden"][name="filter.v.availability"]'
  );
  if (forcedAvailability) {
    params.set("filter.v.availability", forcedAvailability.value);
  }

  window.history.replaceState(
    {},
    "",
    `${window.location.pathname}?${params.toString()}`
  );
  await reloadCollection();
};

/* =====================
   Initialise les curseurs du filtre de prix
   ===================== */
const initializePriceFilterSliders = () => {
  document.querySelectorAll(".amount-selection-slider").forEach((slider) => {
    const minValueField = slider
      .closest("form")
      .querySelector('[name="filter.v.price.gte"]');
    const maxValueField = slider
      .closest("form")
      .querySelector('[name="filter.v.price.lte"]');

    if (slider.noUiSlider) {
      slider.noUiSlider.destroy();
    }

    if (!window.noUiSlider || !minValueField || !maxValueField ||
        Number(slider.dataset.rangeMax) <= Number(slider.dataset.rangeMin)) return;
    noUiSlider.create(slider, {
      start: [Number(slider.dataset.minValue), Number(slider.dataset.maxValue)],
      connect: true,
      range: {
        min: Number(slider.dataset.rangeMin),
        max: Number(slider.dataset.rangeMax),
      },
    });

    slider.noUiSlider.on("update", (values) => {
      minValueField.value = values[0];
      maxValueField.value = values[1];
    });

    slider.noUiSlider.on("change", () => {
      minValueField.dispatchEvent(new Event("input"));
    });
  });
};
initializePriceFilterSliders();
window.addEventListener(
  "onCollectionShopiwebUpdate",
  initializePriceFilterSliders
);

/* =====================
   Met à jour les paramètres de l'URL
   ===================== */
const updateQueryParameters = (key, value) => {
  const params = new URLSearchParams(window.location.search);
  params.delete("page");
  params.set(key, value);
  return `${window.location.pathname}?${params.toString()}`;
};

/* =====================
   Gère les changements dans le tri de la collection
   ===================== */
window.onChangeCollectionSortBy = (sortByValue) => {
  const updatedUrl = updateQueryParameters("sort_by", sortByValue);
  window.history.replaceState({}, "", updatedUrl);
  reloadCollection();
};

/* =====================
   Fonctions permettant de charger d'autres produits dans une collection à la suite d'une action de l'utilisateur.
   ===================== */
const showLoadingIndicator = (button) => {
  button.style.width = `${button.offsetWidth + 2}px`;
  button.style.height = `${button.offsetHeight + 2}px`;
  button.innerHTML = `
        <div class="spinner-border mx-auto spinner-border-sm" style="width: 1.2rem; height: 1.2rem" role="status">
            <span class="visually-hidden">En attente...</span>
        </div>
    `;
};

/* =====================
   Regroupe les produits épuisés en fin de liste

   Liquid ne trie que la page en cours : un produit épuisé de la page 1 resterait
   donc avant les produits en stock de la page 2. Après chaque chargement, on
   redescend les cartes épuisées sous les cartes disponibles pour obtenir l'ordre
   global attendu. Aucune carte n'est retirée : les épuisés restent visibles.

   L'ordre relatif de chaque groupe est préservé (appendChild suit l'ordre du
   DOM), ce qui conserve le tri choisi par le marchand.
   ===================== */
const moveSoldOutProductsLast = () => {
  const productListing = document.querySelector(".collection .product-listing");
  if (!productListing) return;

  const soldOutItems = productListing.querySelectorAll(
    ':scope > .product-item[data-available="false"]'
  );

  soldOutItems.forEach((item) => productListing.appendChild(item));
};

const updateCollectionContent = async (button, direction) => {
  if (paginationBusy || collectionRequest) return;
  const url = button.dataset[direction + "Url"];
  if (!url) return;
  paginationBusy = true;
  const revision = collectionRevision;
  const oldHtml = button.innerHTML;
  const oldStyle = button.getAttribute("style");
  button.disabled = true;
  button.setAttribute("aria-busy", "true");
  showLoadingIndicator(button);
  document.querySelector("#collection-load-error")?.remove();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const doc = await fetchCollectionDocument(url, controller.signal);
    if (revision !== collectionRevision) return;
    const listing = document.querySelector(".collection .product-listing");
    const incoming = doc.querySelector(".collection .product-listing");
    if (!listing || !incoming) throw new Error("Missing product listing");
    listing.insertAdjacentHTML(direction === "next" ? "beforeend" : "afterbegin", incoming.innerHTML);
    moveSoldOutProductsLast();
    // Update only the boundary that moved. Loading an earlier page must not
    // reset the next URL to a page which is already present in the list.
    if (direction === "next") {
      const pager = doc.querySelector("#paging-collection");
      if (pager) document.querySelector("#paging-collection")?.replaceWith(pager);
    } else {
      const previous = doc.querySelector("#btn-load-previous");
      if (previous) button.replaceWith(previous);
      else button.remove();
    }
    window.history.replaceState({}, "", url);
    notifyCollectionUpdate();
  } catch (error) {
    if (revision === collectionRevision) {
      autoPaginationObserver?.disconnect();
      collectionError(button);
    }
  } finally {
    clearTimeout(timeout);
    paginationBusy = false;
    if (button.isConnected) {
      button.innerHTML = oldHtml;
      button.disabled = false;
      button.removeAttribute("aria-busy");
      if (oldStyle === null) button.removeAttribute("style");
      else button.setAttribute("style", oldStyle);
    }
  }
};

window.onClickCollectionLoadMore = (button, event) => {
  event.preventDefault();
  return updateCollectionContent(button, "next");
};

window.onClickCollectionLoadPrevious = (button, event) => {
  event.preventDefault();
  return updateCollectionContent(button, "previous");
};

const initializeAutoPagination = () => {
  autoPaginationObserver?.disconnect();
  const pagination = document.querySelector("#paging-collection");
  if (!pagination || !window.IntersectionObserver) return;
  autoPaginationObserver = new IntersectionObserver((entries) => {
    if (paginationBusy || collectionRequest) return;
    if (entries.some((entry) => entry.isIntersecting)) {
      pagination.querySelector(".button-loading-more:not(:disabled)")?.click();
    }
  }, { threshold: 0, rootMargin: "0px 0px -100px 0px" });
  autoPaginationObserver.observe(pagination);
};
initializeAutoPagination();
window.addEventListener("onCollectionShopiwebUpdate", initializeAutoPagination);

// One scroll handler for the current controls; no intervals/listeners added on reload.
const toggleStickyUtilitiesBar = () => {
  const bar = document.querySelector("#collection-controls");
  if (!bar) return;
  const sticky = window.scrollY > 700;
  bar.classList.toggle("sticky-top", sticky);
  bar.classList.toggle("show", sticky);
  const nav = document.querySelector('[id*="__navbar"].sticky-top')?.clientHeight || 0;
  const announcement = document.querySelector('[id*="__announcement-bar"].sticky-top')?.clientHeight || 0;
  bar.style.top = sticky ? `${nav + announcement}px` : "0";
};
document.addEventListener("scroll", window.throttle(toggleStickyUtilitiesBar, 200), { passive: true });
window.addEventListener("onCollectionShopiwebUpdate", toggleStickyUtilitiesBar);
toggleStickyUtilitiesBar();

// This function reveals all color swatches for a product and removes the trigger button
window.showCompleteSwatchSet = (triggerButton, event) => {
  const swatchContainer = triggerButton.closest(".color-swatches");
  swatchContainer.querySelectorAll("li").forEach((swatch) => {
    swatch.removeAttribute("hidden");
  });
  triggerButton.remove();
};

/* =====================
   Bannière de collection
   ===================== */
const initializeCollectionBanners = () => {
  const productListing = document.querySelector(".collection .product-listing");

  if (!productListing) return;

  // Process each banner and insert it before the specified product item
  document.querySelectorAll(".collection-card").forEach((b) => {
    const injectionIndex = Number(b.dataset.inject) - 1; 
    const targetItem = productListing.children[injectionIndex]; 

    if (targetItem) {
      targetItem.insertAdjacentElement("beforebegin", b);
    }
  });
};
initializeCollectionBanners();

// Handle the loading of Shopify sections which might include collection banners
document.addEventListener("shopify:section:load", (event) => {
  if (event.target.querySelector(".collection-card")) {
    // Re-initialize banners upon dynamic content loading to ensure correct placement
    initializeCollectionBanners();
  }
});
